import React from "react";

/**
 * BrandLogo — iconic Future Football™ brand mark.
 * Matches the moodboard reference: ALL-CAPS white wordmark, hexagonal football
 * core with a bright cyan/teal energy rim, and a cyan "FUTURE FOOTBALL™"
 * microline. Sits naturally inside the cosmic universe.
 */
const BrandLogo = ({ onClick, size = "md", showTag = true }) => {
  const small = size === "sm";
  const ballSize = small ? 32 : 42;

  return (
    <button
      data-testid="brand-logo"
      onClick={onClick}
      className="group relative flex items-center gap-3 select-none"
      aria-label="EleKtro Universal — Future Football™"
    >
      {/* Football glow core */}
      <span
        className="relative inline-flex items-center justify-center"
        style={{ width: ballSize + 18, height: ballSize + 18 }}
      >
        {/* Deep outer aura */}
        <span
          aria-hidden
          className="absolute -inset-3 rounded-full opacity-75 blur-2xl"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, rgba(111,246,255,0.55) 0%, rgba(61,191,255,0.42) 35%, rgba(155,77,255,0.3) 60%, rgba(255,61,203,0.18) 80%, transparent 100%)",
          }}
        />
        {/* Bright cyan rim */}
        <span
          aria-hidden
          className="absolute inset-1 rounded-full pulse-glow"
          style={{
            boxShadow:
              "0 0 0 1px rgba(111,246,255,0.6), 0 0 12px rgba(61,191,255,0.85), 0 0 24px rgba(61,191,255,0.55)",
          }}
        />
        {/* Football SVG — IDENTICAL DNA to the floating GlowingFootballs */}
        <svg
          viewBox="0 0 100 100"
          width={ballSize}
          height={ballSize}
          className="relative"
          style={{
            animation: "eu-football-spin 32s linear infinite",
            filter:
              "drop-shadow(0 0 4px rgba(111,246,255,0.95)) drop-shadow(0 0 14px rgba(61,191,255,0.55))",
          }}
        >
          <defs>
            <radialGradient id="bl-core" cx="50%" cy="42%" r="65%">
              <stop offset="0%" stopColor="#15082A" stopOpacity="1" />
              <stop offset="55%" stopColor="#0A0418" stopOpacity="1" />
              <stop offset="100%" stopColor="#05020E" stopOpacity="1" />
            </radialGradient>
            <filter id="bl-bloom" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="0.55" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* Solid dark sphere */}
          <circle cx="50" cy="50" r="44" fill="url(#bl-core)" />
          {/* Glowing cyan seams — same geometry as GlowingFootball */}
          <g
            stroke="#6FF6FF"
            strokeWidth="1.35"
            strokeOpacity="0.95"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#bl-bloom)"
          >
            {/* Centre pentagon */}
            <polygon points="50.00,41.50 58.08,47.37 54.99,58.08 45.01,58.08 41.92,47.37" />
            {/* 5 hexagons on each pentagon edge */}
            {[-54, 18, 90, 162, 234].map((deg, idx) => {
              const θ = (deg * Math.PI) / 180;
              const cx = 50 + Math.cos(θ) * 18;
              const cy = 50 + Math.sin(θ) * 18;
              const r = 9.4;
              const pts = [];
              for (let k = 0; k < 6; k++) {
                const va = θ + Math.PI + ((k * 60 + 30) * Math.PI) / 180;
                pts.push(
                  `${(cx + Math.cos(va) * r).toFixed(2)},${(cy + Math.sin(va) * r).toFixed(2)}`,
                );
              }
              return <polygon key={idx} points={pts.join(" ")} opacity="0.9" />;
            })}
            <circle cx="50" cy="50" r="44" strokeOpacity="0.7" />
          </g>
          {/* Premium glass highlight */}
          <circle cx="38" cy="34" r="2.2" fill="#ffffff" fillOpacity="0.25" filter="url(#bl-bloom)" />
        </svg>

        {/* Three orbiting energy particles */}
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            aria-hidden
            className="absolute inset-0"
            style={{
              animation: `eu-orbit ${10 + i * 3}s linear infinite`,
              animationDelay: `${-i * 2}s`,
            }}
          >
            <span
              className="absolute h-[3px] w-[3px] rounded-full"
              style={{
                top: i === 0 ? "0%" : i === 1 ? "85%" : "20%",
                left: i === 0 ? "85%" : i === 1 ? "10%" : "0%",
                background: "#6FF6FF",
                boxShadow: "0 0 10px #6FF6FF, 0 0 4px #fff",
              }}
            />
          </span>
        ))}
      </span>

      {/* Wordmark — all caps, white, sans-serif (matches moodboard) */}
      <span className="flex flex-col items-start leading-none">
        <span
          className={`font-display font-bold uppercase tracking-[0.06em] text-white ${
            small ? "text-[14px]" : "text-[16px] md:text-[17px]"
          }`}
          style={{
            textShadow:
              "0 0 14px rgba(255,255,255,0.35), 0 0 4px rgba(255,255,255,0.6)",
          }}
        >
          Elektro <span className="font-extrabold">Universal</span>
        </span>
        {showTag && (
          <span
            className={`mt-1.5 font-display uppercase font-medium tracking-[0.32em] ${
              small ? "text-[8.5px]" : "text-[10px]"
            }`}
            style={{
              color: "#6FF6FF",
              textShadow:
                "0 0 8px rgba(111,246,255,0.85), 0 0 18px rgba(61,191,255,0.45)",
            }}
          >
            Future Football™
          </span>
        )}
      </span>
    </button>
  );
};

export default BrandLogo;
